import { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Stack from '@mui/material/Stack';
import { equals, includes, pathOr } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';

import ToggleButtonMui from 'components/atoms/GefMui/ToggleButton';
import codelistProvider from 'components/decorators/codelistProvider';
import ComponentLoader from 'components/molecules/ComponentLoader';
import { useIgnoreErrorsWhen } from 'components/molecules/ComponentLoader/hooks';
import { codelists } from 'constants/codelists';
import fetch from 'features/common/fetch';
import contactFeedback from 'features/views/contactFeedback';
import { CONTACT_NOT_FOUND_ERR_MSG, PARTNER_LOCKED } from 'features/views/contactFeedback/constants';
import { getIdsByPropValue } from 'utils';

import Confirm from './components/Confirm';

const { NEW, ASSIGNED } = contactFeedback.constants.FEEDBACK_ID;

export const Feedback = ({ solutionStates, contact = {}, onSuccess }) => {
	const dispatch = useDispatch();

	const campaignId = pathOr(-1, ['contact', 'campaign', 'id'])({ contact });
	const campaignStates = useSelector(contactFeedback.selectors.getCampaignStates(campaignId)); // this holds possible feedbacks
	const selectedStateId = useSelector(contactFeedback.selectors.getSelectedFeedbackStateId); // selected feedback id

	const fetchCampaignStates = useCallback(() => {
		const { campaign = {} } = contact;
		campaign.id && dispatch(contactFeedback.actions.getCampaignStates(campaign.id));
	}, [dispatch, contact]);

	useEffect(() => {
		fetchCampaignStates();
	}, [fetchCampaignStates]);

	useEffect(
		() => () => {
			dispatch(contactFeedback.actions.removeFeedbackState());
		},
		[dispatch],
	);

	const checkIfSuccess = useCallback(
		(Id) => {
			const successIds = getIdsByPropValue(solutionStates, 'stateType', 'SUCCESSFUL');
			return successIds.includes(Id);
		},
		[solutionStates],
	);

	const checkIfFail = (Id) => {
		const failIds = getIdsByPropValue(solutionStates, 'stateType', 'FAIL');
		return failIds.includes(Id);
	};

	const checkUnlockRequired = useCallback(
		(solutionStateId) => {
			const unlockCheckRequiredIds = getIdsByPropValue(solutionStates, 'unlockCheck', true);
			return unlockCheckRequiredIds.includes(solutionStateId);
		},
		[solutionStates],
	);

	const checkIfAllDisabled = (solutionStateId, statusId) => {
		const successIds = getIdsByPropValue(solutionStates, 'stateType', 'SUCCESSFUL');
		const IdsForDisabledButtons = [NEW, ASSIGNED, ...successIds];
		return IdsForDisabledButtons.includes(solutionStateId) ? true : equals(solutionStateId, statusId);
	};

	const checkSolutionStateId = (statusId, solutionStateId) => {
		const failIds = getIdsByPropValue(solutionStates, 'stateType', 'FAIL');
		if (failIds.includes(solutionStateId)) {
			return !(checkIfSuccess(statusId) || checkIfFail(statusId));
		} else {
			return checkIfAllDisabled(solutionStateId, statusId);
		}
	};

	const handleReload = useCallback(() => {
		fetchCampaignStates();
	}, [fetchCampaignStates]);

	const handleSelectFeedback = useCallback(
		(feedbackStateId) => () => {
			if (checkUnlockRequired(feedbackStateId) && !contact.isCcdClient) {
				dispatch(contactFeedback.actions.showPartnerNotFoundInCCDDialog());
				return;
			}

			if (!checkIfSuccess(feedbackStateId)) {
				// updates feedback of type != success
				dispatch(contactFeedback.actions.updateFeedback(contact, feedbackStateId, onSuccess));
			}

			// If we select feedback other then success we want to update contact on BE without user confirmation
			dispatch(contactFeedback.actions.selectedFeedbackState(feedbackStateId)); // updates feedback reducer
		},
		[dispatch, checkIfSuccess, checkUnlockRequired, contact, onSuccess],
	);

	const handleCancel = useCallback(() => {
		dispatch(contactFeedback.actions.removeFeedbackState()); // updates feedback reducer
	}, [dispatch]);

	const handleConfirm = useCallback(() => {
		// updates feedback of type success
		dispatch(contactFeedback.actions.updateFeedbackSuccess(contact, selectedStateId));
	}, [dispatch, contact, selectedStateId]);

	const { solutionStateId } = contact;

	// selectedStateId - user selection
	// solutionStateId - default selection on contact
	const selectedFeedback = selectedStateId || solutionStateId;

	const ignoreErrorsForPartnerLocked = useIgnoreErrorsWhen({
		fetchStorePath: `${fetch.constants.urls.contactDetail}.feedback`,
		attributePath: ['metaData', 'parsedContent', 'type'],
		value: PARTNER_LOCKED,
	});

	const ignoreErrorsForContactNotFound = useIgnoreErrorsWhen({
		fetchStorePath: `${fetch.constants.urls.contactDetail}.feedback`,
		attributePath: ['metaData', 'parsedContent', 'message'],
		condition: (value) => includes(CONTACT_NOT_FOUND_ERR_MSG, value),
	});

	return (
		<ComponentLoader
			fetchStorePath={`${fetch.constants.urls.contactDetail}.feedback`}
			ignoreErrors={ignoreErrorsForPartnerLocked || ignoreErrorsForContactNotFound}
		>
			<ComponentLoader onReload={handleReload} fetchStorePath={fetch.constants.urls.campaignStatus}>
				<Stack spacing={3}>
					<Stack
						direction="row"
						spacing={2}
						useFlexGap
						sx={{
							flexWrap: 'wrap',
						}}
					>
						{campaignStates.map((item) => {
							// If contact is in SUCCESS feedback we cant change it to another feedback type
							// so we will disable all other feedbacks with disabled prop
							// and highlight SUCCESS type feedback with selected prop
							const isSelected = selectedFeedback === item.statusId;
							const isDisabled = checkSolutionStateId(item.statusId, solutionStateId);
							return (
								<ToggleButtonMui
									key={item.id}
									label={item.statusText}
									onClick={handleSelectFeedback(item.statusId)}
									selected={isSelected}
									disabled={isDisabled}
								/>
							);
						})}
					</Stack>
					{checkIfSuccess(selectedStateId) && <Confirm onCancel={handleCancel} onConfirm={handleConfirm} />}
				</Stack>
			</ComponentLoader>
		</ComponentLoader>
	);
};

Feedback.propTypes = {
	contact: PropTypes.object,
	mui: PropTypes.bool,
	onSuccess: PropTypes.func,
	solutionStates: PropTypes.array, // this holds which feedbacks are success type
};

export default codelistProvider(codelists.solutionState, 'solutionStates')(Feedback);
